<template>
  <component
    :is="tag"
    class="play-card"
    :class="[
      `play-card--${variant}`,
      `play-card--${size}`,
      { 'play-card--interactive': interactive },
    ]"
    v-bind="$attrs"
    v-on="$listeners"
  >
    <!-- VUE3-MIGRATION: $listeners removed in Vue 3 (merged into $attrs).
       On migration delete `v-on="$listeners"` — v-bind="$attrs" forwards listeners too. -->
    <div
      v-if="$slots.header"
      class="play-card__header"
    >
      <slot name="header" />
    </div>

    <div class="play-card__body">
      <slot />
    </div>

    <div
      v-if="$slots.footer"
      class="play-card__footer"
    >
      <slot name="footer" />
    </div>
  </component>
</template>

<script>
/**
 * PlayCard — surface primitive for the Play system component layer.
 *
 * Token-driven (see src/styles/tokens.scss): no hardcoded colors, so a
 * palette swap requires zero changes here. Replaces bootstrap-vue's
 * <b-card> in the rebrand.
 *
 * Variants:
 *   default — paper surface, thick ink border + signature hard offset shadow
 *   flat    — hairline border, no hard shadow (nested rows: slots, list items)
 *   ink     — dark panel (surface-ink), text-on-ink, soft ambient shadow
 *
 * Optional `header` / `footer` slots get a hairline divider. Set `interactive`
 * for clickable cards (hover lift + press feel + pointer cursor).
 *
 * Renders as <div> by default, or any tag via `tag` (article/section/a/
 * router-link) so it works for static panels and clickable targets alike.
 *
 * VUE3-MIGRATION: `v-on="$listeners"` in the template is the only Vue 2-ism
 * here. In Vue 3 $listeners is removed (merged into $attrs); drop that line
 * and v-bind="$attrs" keeps forwarding listeners. The props/slots contract is
 * already Vue 3-safe.
 */
export default {
  name: 'PlayCard',
  inheritAttrs: false,
  props: {
    /** Surface role: bordered paper, flat hairline, or dark ink panel */
    variant: {
      type: String,
      default: 'default',
      validator: v => ['default', 'flat', 'ink'].includes(v),
    },
    /** Controls inner padding + corner radius */
    size: {
      type: String,
      default: 'md',
      validator: v => ['sm', 'md', 'lg'].includes(v),
    },
    /** Element to render: 'div' | 'article' | 'section' | 'a' | 'router-link' */
    tag: {
      type: String,
      default: 'div',
    },
    /** Adds hover lift, press feel and pointer cursor for clickable cards */
    interactive: {
      type: Boolean,
      default: false,
    },
  },
}
</script>

<style lang="scss" scoped>
.play-card {
  display: block;
  position: relative;
  background: var(--surface-card);
  color: var(--text-body);
  border-radius: var(--radius-xl);
  text-decoration: none;

  &__header {
    margin-bottom: 16px;
    padding-bottom: 16px;
    border-bottom: var(--border-w) solid var(--line);
  }

  &__footer {
    margin-top: 16px;
    padding-top: 16px;
    border-top: var(--border-w) solid var(--line);
  }

  /* ── Sizes (padding + radius) ── */
  &--sm { padding: 16px; border-radius: var(--radius-lg); }
  &--md { padding: 24px; }
  &--lg { padding: 32px; }

  /* ── Variants ── */
  &--default {
    border: var(--border-w-bold) solid var(--border-ink);
    box-shadow: var(--shadow-hard-lg);
  }

  &--flat {
    border: var(--border-w) solid var(--line);
  }

  &--ink {
    background: var(--surface-ink);
    color: var(--text-on-ink);
    box-shadow: var(--shadow-soft);
  }

  /* ── Interactive (clickable cards) ──
     Mirrors the Play press feel: shadow grows + lifts on hover,
     collapses on active. Tilt/parallax lives in a future v-tilt directive. */
  &--interactive {
    cursor: pointer;
    transition:
      transform var(--dur) var(--ease-bounce),
      box-shadow var(--dur) var(--ease-bounce),
      border-color var(--dur) var(--ease-out);
    will-change: transform;
    -webkit-tap-highlight-color: transparent;
  }

  &--interactive.play-card--default {
    &:hover { transform: translateY(-2px); box-shadow: 0 14px 0 var(--border-ink); }
    &:active { transform: translateY(3px); box-shadow: var(--shadow-press); }
  }

  &--interactive.play-card--flat {
    &:hover { border-color: var(--border-ink); transform: translateY(-2px); }
    &:active { transform: translateY(1px); }
  }

  &--interactive.play-card--ink {
    &:hover { transform: translateY(-2px); }
    &:active { transform: translateY(1px); }
  }
}
</style>
